import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useRef,
} from 'react'

// @mui material components
import Card from '@mui/material/Card'

// Soft UI Dashboard PRO React components
import SoftBox from 'components/SoftBox'
import SoftTypography from 'components/SoftTypography'
import SoftSelect from 'components/SoftSelect'
import { Box, MenuItem, Select, Typography } from '@mui/material'

// Soft UI Dashboard PRO React icons
import SpaceShip from 'components/Icons/SpaceShip'
import {
  AccountBox,
  Article,
  AttachMoney,
  Balance,
  BeachAccess,
  CalendarMonth,
  Description,
  Favorite,
  Feed,
  Flag,
  HealthAndSafety,
  Label,
  ListAlt,
  LocalHospital,
  MilitaryTech,
  Paid,
  People,
  Receipt,
  RocketLaunch,
  Spa,
  Task,
  Terrain,
  TrendingUp,
  Work,
} from '@mui/icons-material'

const QuickNav = forwardRef(
  (
    { intakeState, isMobile, showUnion = true, showSurvivorBenefit = true },
    ref
  ) => {
    const [scrollPosition, setScrollPosition] = useState(0)
    const [tabYValues, setTabYValues] = useState([])
    const [currentTab, setCurrentTab] = useState('basic-info')
    const isGovernmentEmployee = intakeState?.governmentEmployee
    // console.log('isGovernmentEmployee', isGovernmentEmployee)
    const updateTabLocations = () => {
      // console.log('Updating Tab Locations...')
      const tabs = document.getElementsByClassName('settings-tab')
      const newTabYValues = []

      Array.from(tabs).forEach((tab) => {
        const rect = tab.getBoundingClientRect()
        const tabYPosition = rect.top + window.scrollY
        newTabYValues.push({ href: tab.id, y: tabYPosition })
      })

      setTabYValues(newTabYValues)
    }

    //allows us to call updateTabLocations from the profile page
    //called when page layout changes
    useImperativeHandle(ref, () => ({
      updateTabLocations,
    }))

    useEffect(() => {
      const handleScroll = () => {
        const position = window.scrollY
        setScrollPosition(position)

        const isAtBottom =
          position + window.innerHeight >=
          document.documentElement.scrollHeight - 50

        requestAnimationFrame(() => {
          tabYValues.forEach((tab, index) => {
            const isLastTab = index === tabYValues.length - 1

            if (isLastTab) {
              if (position >= tab.y - 100 && position <= tab.y + 100) {
                setCurrentTab(tab.href)
              }
            } else {
              if (position >= tab.y - 150 && position <= tab.y + 150) {
                setCurrentTab(tab.href)
              }
            }
          })

          if (isAtBottom && tabYValues.length) {
            setCurrentTab(tabYValues[tabYValues.length - 1].href)
          }
        })
      }

      window.addEventListener('scroll', handleScroll, { passive: true })

      return () => {
        window.removeEventListener('scroll', handleScroll)
      }
    }, [scrollPosition, tabYValues])

    const scrollToSection = (event) => {
      const selectedTab = event.target.value

      const targetSection = document.getElementById(selectedTab)
      if (targetSection) {
        targetSection.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        })
      }
    }

    const sidenavItems = [
      {
        label: 'Quick Nav',
        href: 'quick',
        icon: <RocketLaunch />,
        displayItem: true,
      },
      {
        label: 'Personal Information',
        href: 'basic-info',
        icon: <AccountBox />,
        displayItem: true,
      },
      {
        label: 'Employment',
        href: 'employment',
        icon: <Work />,
        displayItem: true,
      },
      {
        label: 'Military Service',
        href: 'military',
        icon: <MilitaryTech />,
        displayItem: true,
      },
      { label: 'Family', href: 'family', icon: <People />, displayItem: true },
      {
        label: 'Leave & Earnings',
        href: 'paystub',
        icon: <Paid />,
        displayItem: isGovernmentEmployee,
      },
      {
        label: isGovernmentEmployee
          ? 'TSP Contributions'
          : '401k Contributions',
        href: isGovernmentEmployee ? 'tsp' : '401k',
        icon: <Terrain />,
        displayItem: true,
      },
      {
        label: 'Healthcare',
        href: 'healthcare',
        icon: <HealthAndSafety />,
        displayItem: true,
      },
      {
        label: isGovernmentEmployee ? 'Deductions & Net Pay' : 'Net Pay',
        href: 'deductions',
        icon: <Receipt />,
        displayItem: true,
      },
      {
        label: isGovernmentEmployee
          ? 'FEGLI & Life Insurance'
          : 'Life Insurance',
        href: 'fegli',
        icon: <LocalHospital />,
        displayItem: true,
      },
      {
        label: 'Union Affiliation',
        href: 'union',
        icon: <Label />,
        displayItem: showUnion,
      },
      {
        label: 'Survivor Benefit',
        href: 'survivorbenefit',
        icon: <AttachMoney />,
        displayItem: showSurvivorBenefit && isGovernmentEmployee,
      },
      {
        label: 'Balance Sheet',
        href: 'balancesheet',
        icon: <Balance />,
        displayItem: true,
      },
      {
        label: 'TSP Allocation',
        href: 'tspAllocation',
        icon: <TrendingUp />,
        displayItem: isGovernmentEmployee,
      },
      { label: 'Goals', href: 'goals', icon: <Flag />, displayItem: true },
      {
        label: 'Social Security',
        href: 'socialsecurity',
        icon: <Feed />,
        displayItem: true,
      },
      { label: 'Taxes', href: 'taxes', icon: <Task />, displayItem: true },
      {
        label: 'Budgeting',
        href: 'budgeting',
        icon: <ListAlt />,
        displayItem: true,
      },
      {
        label: 'Income Statement',
        href: 'incomestatement',
        icon: <Article />,
        displayItem: true,
      },
      {
        label: 'To-Do List',
        href: 'todolist',
        icon: <Description />,
        displayItem: true,
      },
      {
        label: 'Schedule Appointment',
        href: 'scheduleAppointment',
        icon: <CalendarMonth />,
        displayItem: true,
      },
      { label: 'Other', href: 'other', icon: <Spa />, displayItem: true },
    ]

    const renderSidenavItems = sidenavItems.map(
      ({ icon, label, href }, key) => {
        const itemKey = `item-${key}`

        return (
          <Box key={itemKey}>
            <SoftTypography
              component="a"
              href={`#${href}`}
              variant="button"
              fontWeight="regular"
              color="text"
              textTransform="capitalize"
              sx={({
                borders: { borderRadius },
                functions: { pxToRem },
                palette: { light },
                transitions,
              }) => ({
                display: 'flex',
                alignItems: 'center',
                borderRadius: borderRadius.md,
                backgroundColor: currentTab === href ? light.main : 'none',
                fontWeight: currentTab === href ? 'bold' : 'regular',
                padding: `${pxToRem(10)} ${pxToRem(0)}`,
                transition: transitions.create('background-color', {
                  easing: transitions.easing.easeInOut,
                  duration: transitions.duration.shorter,
                }),

                '&:hover': {
                  backgroundColor: light.main,
                  fontWeight: 'bold',
                },
              })}
            >
              <Box mr={1.5} pl={1.5} lineHeight={1} sx={{ fontSize: '20px' }}>
                {icon}
              </Box>
              {label}
            </SoftTypography>
          </Box>
        )
      }
    )

    return (
      <>
        <Select
          MenuProps={{
            style: {
              // maxHeight: 350,
            },
          }}
          sx={{
            position: 'fixed',
            width: 'inherit !important',
            zIndex: 1100,
            boxShadow: '0px 4px 25px -8px rgba(0,0,0,0.2)',
            border: 'none',
            padding: '0.5rem 0.75rem !important',
            '&>div.MuiSelect-select': {
              width: '100% !important',
              padding: '0 !important',
            },
            '&>svg': {
              fontSize: '1.5rem !important',
              display: 'inline-block',
            },
          }}
          value={'quick'}
          onChange={scrollToSection}
        >
          {sidenavItems.map(({ label, href, icon, displayItem }, key) => (
            <MenuItem
              key={key}
              value={href}
              sx={{
                display: `${displayItem ? 'flex' : 'none'}`,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  fontSize: '18px',
                }}
              >
                {icon ? icon : <></>}

                <Typography
                  ml={1}
                  fontWeight="semi-bold"
                  color="text"
                  variant="body2"
                  fontSize="18px"
                >
                  {label}
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </>
    )
  }
)

QuickNav.displayName = 'QuickNav'

export default QuickNav
